import { AlertsApiService } from 'core/api';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import { useCollection } from 'core/hooks/useCollection';
import { IAlertDao } from 'core/api/types/alerts.interface';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import AcknowledgeAlertDialog from './dialogs/acknowledge-alert-dialog';
import { PageElement } from 'core/utilities/interface-helpers';
import { alertTemplates } from 'modules/settings/vehicles/templates';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Checkbox from '@atlaskit/checkbox';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import ExportDialog from 'modules/dialogs/export-dialog';
import { useFlags } from '@atlaskit/flag';
import { orderBy, QueryConstraint, Timestamp, where } from 'firebase/firestore';
import { IButton } from 'shared/components/buttons/button-interface';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import dayjs from 'dayjs';

const AlertsTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Alert reason', key: 'reason', templateId: 'text', width: 10 },
  { label: 'Triggered by', key: 'triggeredBy', templateId: 'triggeredBy', width: 10 },
  { label: 'Triggered at', key: 'triggeredAt', templateId: 'triggeredAt', width: 10 },
  { label: 'Acknowledged', key: 'acknowledged', templateId: 'acknowledged', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const AlertsList = () => {
  const dialog = useDialog();
  const flags = useFlags();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<IAlertDao>({
    apiService: AlertsApiService,
    constraints: [...constraints, orderBy('triggered.at', 'desc')],
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '')
        .map((filter) => {
          if (filter.id.includes('From')) {
            const date = Timestamp.fromDate(
              dayjs(filter.value as string)
                .startOf('day')
                .toDate()
            );
            return where('triggered.at', '>=', date);
          } else if (filter.id.includes('To')) {
            const date = Timestamp.fromDate(
              dayjs(filter.value as string)
                .endOf('day')
                .toDate()
            );
            return where('triggered.at', '<=', date);
          }

          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (alert: IAlertDao) => {
    return <Checkbox isChecked={selectedRows.includes(alert.uid)} onChange={(e) => rowChecked(e, alert.uid)} />;
  };

  const actionCellTemplate = (alert: IAlertDao) => (
    <div className='w-full flex justify-end space-x-2'>
      {alert.acknowledged ? null : (
        <SharedButton
          onClick={() => dialog?.openDialog(<AcknowledgeAlertDialog alert={alert} />)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Acknowledge'
        />
      )}
      <SharedButton
        onClick={() =>
          dialog?.openDialog(
            <ConfirmationDialog
              title='Delete alert'
              textContent='Are you sure you want to delete this alert? This action cannot be undone.'
              action={() => deleteItem(AlertsApiService, alert, flags, dialog)}
              buttonLabel='Delete Alert'
            />
          )
        }
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Delete'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'action',
    },
    { template: selectCellTemplate, id: 'select' },
  ];

  const headerActions: PageElement[] = [
    {
      key: 'exportAlerts',
      element: (
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ExportDialog
                title='Export Alerts'
                apiService={AlertsApiService}
                functionName={FirebaseFunctionNames.EXPORT_ALERTS}
                filterQuery={filterQuery}
              />
            )
          }
          type='button'
          appearance='default'
          label='Export Alerts'
        />
      ),
    },
    ...(selectedRows.length
      ? [
          {
            key: 'deleteAlerts',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete alerts'
                      textContent='Are you sure you want to delete these alerts? This action cannot be undone.'
                      action={() => deleteItems(AlertsApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Alerts'
                    />
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Alerts'
              />
            ),
          },
        ]
      : []),
  ];

  const filters: Filter[] = [
    {
      id: 'dateFrom',
      label: 'Triggered Date From',
      date: true,
    },
    {
      id: 'dateTo',
      label: 'Triggered Date To',
      date: true,
    },
    {
      id: 'acknowledged',
      label: 'Acknowledged',
      options: [
        { value: 'true', label: 'Acknowledged' },
        { value: 'false', label: 'Not Acknowledged' },
      ],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Alerts' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={AlertsTableColumns}
        rows={tableRows}
        isLoading={loading}
        customTemplates={[...customTemplates, ...alertTemplates]}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default AlertsList;
